import React, { useEffect, useState } from "react";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { CssBaseline, Paper, Typography, withStyles, WithStyles } from "@material-ui/core";
import { isEmailValid } from "../../../utilities/generalUtils";
import { convertDateStringToNumber, today } from "../../../utilities/dateTimeUtils";
import { useDispatch, useSelector } from "react-redux";
import { findProductByName } from "../productOverview/ProductHelper";
import { useHistory, useParams } from "react-router-dom";
import * as A7ProductsRequestAction from "../../../stores/a7products/A7ProductsRequestAction";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { styles } from "./SampleDataView.styles";
import { DATE_FORMAT } from "../../../constants/TIME";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { isEuCountry } from "../../../utilities/countriesUtils";
import { SampleDataForm } from "./SampleDataForm";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface ISampleDataViewProps extends WithStyles<typeof styles> {
}

const VALIDATION_VAT = "VAT must be set";
const VALIDATION_EMAIL = "Email is not valid";

const SampleDataViewPure: React.FunctionComponent<ISampleDataViewProps> = ({classes}) => {

    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();
    const dispatch = useDispatch();

    const countries = useSelector(state => selectVisibleCountries(state));

    const [errors, setErrors] = useState({} as any);

    const a7productsInfoFromState = useSelector((state) => state.a7products);
    const sampleProductsInfo = {
        products: a7productsInfoFromState?.products?.filter(product => !product.id.includes("A7")),
        categories: a7productsInfoFromState?.categories?.filter(category => !category.id.includes("A7"))
    };

    const {productIdentifier} = useParams();
    const thisProduct = findProductByName(sampleProductsInfo?.products, productIdentifier);
    const thisCategory = findProductByName(sampleProductsInfo?.categories, thisProduct?.category);

    const availableProducts = thisProduct?.subProducts?.map(p => ({name: p.productName, id: p.productId, price: p.price}));
    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);

    const [details, setDetails] = React.useState({
        title: "",
        firstName: "",
        lastName: "",
        companyName: "",
        companyPhone: "",
        companyEmail: "",
        street: "",
        postalCode: "",
        city: "",
        country: "",
        vat: "",
        product: thisProduct?.id,
        subProductId: "",
        desiredEffectiveDate: today().format(DATE_FORMAT),
        message: "",
        termsConditions: "",
        confirmLegalEntity: "",
        //honeypot
        website: "",
    });

    const handleChange = event => {
        const name = event.target.name;
        let value = event.target.value;
        if (name === "subProductId") {
            value = availableProducts?.find(p => p.name === value).id;
        }
        setDetails({
            ...details,
            [name]: value
        });
        let newErrors: any = {...errors};
        if (name === "companyEmail") {
            if (value) {
                newErrors[name] = isEmailValid(value) ? "" : VALIDATION_EMAIL;
            }
        }
        if (name === "country") {
            newErrors["vat"] = isEuCountry(countries, value) && details.vat === "" ? VALIDATION_VAT : "";
        }
        if (name === "vat") {
            newErrors["vat"] = isEuCountry(countries, details.country) && value === "" ? VALIDATION_VAT : "";
        }

        setErrors({
            ...newErrors
        });
    };

    const sendContactRequest = event => {
        event.preventDefault();
        const {vat, desiredEffectiveDate, website, ...filteredDetails} = details;
        const request = {
            ...filteredDetails,
            tin: details.vat,
            effectiveDate: convertDateStringToNumber(details.desiredEffectiveDate)
        };
        if (details.website === "") {
            dispatch(A7ProductsRequestAction.makeRequest(request)).then(() => {
                enqueueSnackbar("Message has been sent", {
                    variant: ToastStatusEnum.Info
                });
                history.push("/sample-data/success");
            });
        }
    };

    useEffect(() => {
        if (details.product === "" && Array.isArray(availableProducts) && availableProducts.length > 0) {
            setDetails(details => ({
                ...details,
                product: availableProducts[0].id
            }));
        }
    }, [details.product, availableProducts]);

    useEffect(() => {
        if (userDetails) {
            setDetails(details => ({
                ...details,
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                companyName: userDetails.company,
                companyEmail: userDetails.email,
                companyPhone: userDetails.phone,
                vat: userDetails.tin,
                street: userDetails.billingAddress.addressLine1,
                postalCode: userDetails.billingAddress.postcode,
                city: userDetails.billingAddress.city,
                country: userDetails.billingAddress.country,
            }))
        }
    }, [userDetails]);

    return (
        <>
            <CenteredContainer>
                <Breadcrumbs
                    items={[
                        {title: "Product overview", href: "/products"},
                        {title: thisProduct?.name}
                    ]}
                />
                <CssBaseline />
                <div className={classes.content}>
                    <Typography variant="h1">{thisCategory?.name}</Typography>
                    <Typography variant="body1" className={classes.description}>{thisCategory?.description}</Typography>
                    <Paper className={classes.background}>
                        <SampleDataForm
                            details={details}
                            errors={errors}
                            handleChange={handleChange}
                            product={thisProduct}
                            handleSubmit={sendContactRequest}
                            hasNonEditableFields={userDetails !== undefined}
                        />
                    </Paper>
                </div>
            </CenteredContainer>

        </>
    );
};

export const SampleDataView = withStyles(styles)(SampleDataViewPure);
